import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";

const LOGO_URL = "/img/logo.jpeg";

const links = [
  { to: "/", label: "Home", id: NAV.home },
  { to: "/know-your-disease", label: "Know Your Disease", id: NAV.disease },
  { to: "/know-your-medications", label: "Medications", id: NAV.medications },
  { to: "/diet", label: "Diet", id: NAV.diet },
  { to: "/exercise", label: "Exercise", id: NAV.exercise },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const scrollToBooking = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById("book");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 glass-nav border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3"
          onClick={() => setOpen(false)}
        >
          <img
            src={LOGO_URL}
            alt="Namya Health logo"
            className="h-14 w-14 rounded-lg object-contain"
          />
          <div className="leading-tight">
            <div className="font-serif-display text-2xl font-semibold text-slate-900">
              Namya Health
            </div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500">
              Healing beyond pills
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[hsl(var(--primary-bold))]"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/#book" onClick={scrollToBooking}>
            <Button
              data-testid={NAV.bookCta}
              className="rounded-full px-6 h-11 bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90 shadow-sm"
            >
              Book Appointment
            </Button>
          </Link>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
          className="lg:hidden p-2 rounded-md text-slate-700"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200/70 bg-white/95 backdrop-blur">
          <div className="px-6 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.id}-mobile`}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-base ${
                    isActive
                      ? "bg-slate-100 text-[hsl(var(--primary-bold))]"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/#book"
              onClick={scrollToBooking}
              className="mt-2"
            >
              <Button
                data-testid={`${NAV.bookCta}-mobile`}
                className="w-full rounded-full bg-[hsl(var(--primary-bold))] text-slate-900 font-semibold hover:bg-[hsl(var(--primary-bold))]/90"
              >
                Book Appointment
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
